// src/prototype/data/adapters/enterpriseSpineTypes.ts
// ============================================================
// ENTERPRISE PROTOTYPE — OVER-TIME SPINE · TYPES
//
// Phase 1 (data layer only, no UI). Types shaped to the BigQuery target schemas so live
// data can swap in behind the adapter with no consumer change (same convention as the
// existing use-case content adapter). Aligned to docs/dva-bigquery-data-model.{json,md}
// where a target table already exists; net-new where it does not (governance / as-of do
// not exist in the warehouse today — see docs/dva-mainapplayout-salvage-inventory.md).
//
// Client is ALWAYS "200" (ACME). Never inherit the admin tree's hardcoded '101' / 1.
// ============================================================

/** Golden demo client. client_id is ALWAYS a string. */
export const ENTERPRISE_CLIENT_ID = '200';

/**
 * The "funded line": the top-N initiatives by rank are treated as funded (a stand-in for
 * the scenario budget cap). Crossing it between runs is what makes a portfolio delta
 * meaningful. Kept as a single knob so it can later come from a scenario's budget_cap.
 */
export const FUNDING_LINE_TOP_N = 6;

// ---- the 4-dimension taxonomy (BQ target field names) -----------------------

/**
 * Owner — accountable name + org / service line for an initiative.
 * TARGET: use-case-profile / org-assignment fields (net-new "owner" data · Sim today,
 * see PRD §7.1 P-3). Carried on the initiative so it is stable across runs.
 */
export interface Owner {
  name: string;
  org: string;
}

/** Per-initiative dimension scores, 0–100. Field names match the 4-dimension model. */
export interface DimensionScores {
  financial_impact: number;
  operational_urgency: number;
  strategic_alignment: number;
  implementation_feasibility: number;
}

/** Scenario weight adjustments per dimension (deltas on a 0–1 weight, may be negative). */
export interface DimensionWeightDeltas {
  financial_impact: number;
  operational_urgency: number;
  strategic_alignment: number;
  implementation_feasibility: number;
}

/** Lifecycle status used on decision-log transitions. */
export type UseCaseStatusValue =
  | 'CANDIDATE'
  | 'SHORTLISTED'
  | 'APPROVED'
  | 'FUNDED'
  | 'DEFERRED'
  | 'ON_HOLD'
  | 'KILLED';

/** Governance decision verbs. */
export type DecisionValue = 'APPROVE' | 'DEFER' | 'KILL' | 'HOLD';

// =============================================================================
// BigQuery-SHAPED ROW TYPES
// =============================================================================

/**
 * ScoringRun — one row per scoring run for a client.
 * TARGET: net-new `scoring_runs` table. Today runs are implied by
 * client_use_case_scores.scoring_run_id (there is no first-class run registry yet).
 */
export interface ScoringRun {
  run_id: string;
  client_id: string;
  label: string;
  created_at: string; // ISO 8601
  /** Set when this run was produced under a saved scenario. */
  scenario_id?: string;
}

/**
 * RunScore — one row per (run, initiative): the score/rank an initiative held at a run.
 * TARGET: dva_dataset.client_use_case_scores
 *   run_id ← scoring_run_id · composite ← total_score · confidence ← confidence_level
 *   rank ← rank · use_case_id ← use_case_id.
 * dimension_scores are the 4-dimension roll-up (measure_results aggregated per dimension).
 */
export interface RunScore {
  run_id: string;
  use_case_id: number;
  rank: number;
  composite: number; // 0–100
  confidence: number; // 0–1
  dimension_scores: DimensionScores;
}

/**
 * Scenario — a saved what-if lever set applied on top of a baseline run.
 * TARGET: net-new `scenarios` table (scenario modeling does not exist in the warehouse).
 */
export interface Scenario {
  scenario_id: string;
  client_id: string;
  name: string;
  base_run_id: string;
  levers: {
    weight_deltas: DimensionWeightDeltas;
    budget_cap: number; // $M
    resource_pool?: number; // FTE
  };
  created_at: string; // ISO 8601
}

/**
 * DecisionLogEntry — an append-only, attributed governance decision.
 * TARGET: net-new `client_use_case_decisions` / decision log (no governance store today).
 */
export interface DecisionLogEntry {
  decision_id: string;
  client_id: string;
  use_case_id: number;
  run_id?: string;
  scenario_id?: string;
  decision: DecisionValue;
  rationale: string;
  decided_by: { role: string; name: string };
  decided_at: string; // ISO 8601
  status_before: UseCaseStatusValue;
  status_after: UseCaseStatusValue;
}

// =============================================================================
// VIEW MODELS (what future surfaces consume — the adapter maps rows → these)
// =============================================================================

export interface ScoringRunVM {
  run_id: string;
  label: string;
  created_at: string;
  scenario_id?: string;
  scenario_name?: string;
  initiative_count: number;
}

export interface ScenarioVM {
  scenario_id: string;
  name: string;
  base_run_id: string;
  base_run_label?: string;
  levers: Scenario['levers'];
  created_at: string;
}

export interface DecisionLogEntryVM {
  decision_id: string;
  use_case_id: number;
  title: string;
  decision: DecisionValue;
  rationale: string;
  decided_by: { role: string; name: string };
  decided_at: string;
  run_id?: string;
  scenario_id?: string;
  status_before: UseCaseStatusValue;
  status_after: UseCaseStatusValue;
}

export interface PortfolioInitiativeVM {
  use_case_id: number;
  title: string;
  rank: number;
  composite: number;
  confidence: number;
  dimension_scores: DimensionScores;
  /** Accountable owner (name + org / service line). Carried from the spine. */
  owner: Owner;
  /** Service-line / category the initiative rolls up to. Carried from the spine. */
  category: string;
  /** rank <= FUNDING_LINE_TOP_N */
  funded: boolean;
}

export interface PortfolioRollupVM {
  total_initiatives: number;
  funded_count: number;
  avg_composite: number;
  avg_confidence: number;
  dimension_averages: DimensionScores;
}

export interface PortfolioAsOfVM {
  run: ScoringRunVM;
  funding_line_top_n: number;
  initiatives: PortfolioInitiativeVM[]; // sorted by rank
  rollup: PortfolioRollupVM;
}

/** How an initiative crossed (or held) the funded line between two runs. */
export type StatusDelta =
  | 'STAYS_FUNDED'
  | 'STAYS_UNFUNDED'
  | 'ENTERED_FUNDED'
  | 'DROPPED_BELOW'
  | 'ADDED'
  | 'REMOVED';

/**
 * InitiativeDelta — DERIVED per-initiative change between two runs (never stored).
 * rank_delta = from_rank − to_rank  (positive ⇒ improved / moved up).
 * score_delta = to_composite − from_composite.
 */
export interface InitiativeDelta {
  use_case_id: number;
  title: string;
  rank_delta: number;
  score_delta: number;
  status_delta: StatusDelta;
}

export interface ChangeDeltaVM {
  from_run_id: string;
  to_run_id: string;
  initiatives: InitiativeDelta[];
  summary: {
    moved: number;
    risers: number;
    fallers: number;
    entered_funded: number;
    dropped_below: number;
    top_riser?: InitiativeDelta;
    top_faller?: InitiativeDelta;
  };
}

export interface DecisionLogFilters {
  use_case_id?: number;
  decision?: DecisionValue;
  run_id?: string;
}
